/**
 * The obligations central (ALC-4768): occurrences by period, the gaps and each company's readiness.
 * Namespace `obligationsCentral`.
 */
export const obligationsCentralPtBr = {
  title: 'Central de obrigações',
  subtitle:
    'Acompanhe o que vence, o que está atrasado e o que ainda não foi enquadrado. A configuração é feita na aba Obrigações de cada empresa.',
  tableLabel: 'Obrigações por período',
  gapsTableLabel: 'Lacunas de enquadramento',
  loading: 'Carregando as empresas',
  columns: {
    obligation: 'Obrigação',
    obligationPeriod: 'Obrigação · período',
    companyEstablishment: 'Empresa · estabelecimento',
    due: 'Vencimento',
    state: 'Situação',
    responsible: 'Responsável',
    decision: 'Decisão',
    reason: 'Motivo',
  },
  state: {
    AWAITING_FACTS: 'Aguardando fatos',
    PENDING: 'Pendente',
    PREPARED: 'Preparada',
    VALIDATED: 'Validada',
    TRANSMITTED: 'Transmitida',
    ACCEPTED: 'Aceita',
    RECTIFICATION_PENDING: 'Retificação pendente',
    CLOSED: 'Encerrada',
    NOT_APPLICABLE: 'Não aplicável',
  },
  gapState: {
    PENDING_CLASSIFICATION: 'Pendente de enquadramento',
    OUT_OF_SCOPE_FLAGGED: 'Fora do escopo, sinalizada',
  },
  due: {
    none: 'Sem prazo fixo',
    overdue: 'Atrasada',
    source: 'Fonte: {{source}} (regra {{version}})',
    overrideSource: 'Prazo da empresa. Fonte: {{source}}',
  },
  responsible: {
    none: 'Sem responsável',
    vacant: 'Sem responsável em {{roles}}',
    role: {
      PREPARER: 'preparar',
      APPROVER: 'aprovar',
      TRANSMITTER: 'transmitir',
      PAYER: 'pagar',
    },
  },
  gap: {
    noDecision: 'Sem decisão',
    since: 'desde {{date}}',
  },
  empty: {
    title: 'Nenhuma obrigação neste período',
    description:
      'As ocorrências aparecem quando uma obrigação é decidida como aplicável na aba Obrigações da empresa.',
    gapsTitle: 'Nenhuma lacuna',
    gapsDescription: 'Todas as obrigações têm uma decisão que o ERP consegue cumprir.',
    dueSoonTitle: 'Nada vence nos próximos 7 dias',
    overdueTitle: 'Nenhuma obrigação atrasada',
    unassignedTitle: 'Todas as obrigações em aberto têm responsável',
    indicatorDescription: 'Clique no indicador escolhido para ver todas as obrigações em aberto.',
  },
  loadError: 'Não foi possível carregar as obrigações. Tente novamente.',
  retry: 'Tentar novamente',
  linkLabel: '{{action}}: {{name}}',
  sheet: {
    title: '{{obligation}} · {{period}}',
    description: '{{company}} · {{establishment}}',
    loading: 'Carregando a ocorrência',
    loadError: 'Não foi possível carregar a ocorrência.',
    refreshError: 'Não foi possível atualizar a ocorrência. O que você digitou foi mantido.',
    version: 'Versão {{version}}',
    versionCurrent: 'Versão {{version}} (atual)',
    summary: {
      due: 'Vencimento',
      noDue: 'Sem prazo fixo',
      overdue: 'Atrasada',
      dueDate: '{{date}}',
      dueOverdue: '{{date}} · Atrasada',
      channel: 'Canal de entrega',
      source: 'Fonte do prazo',
      sourceValue: '{{source}} (regra {{version}})',
      overrideValue: 'Prazo da empresa: {{source}}',
      responsibles: 'Responsáveis',
      vacant: 'Ninguém para {{roles}}',
      roleLine: '{{role}}: {{name}}',
    },
    roleLabel: {
      PREPARER: 'Preparar',
      APPROVER: 'Aprovar',
      TRANSMITTER: 'Transmitir',
      PAYER: 'Pagar',
    },
    next: {
      title: 'Próxima etapa',
      advance: 'Avançar para {{state}}',
      reason: 'Motivo ou observação',
      reasonHelp: 'Opcional. Fica registrado no histórico.',
      noAction: 'Esta ocorrência não tem uma próxima etapa.',
      notApplicable:
        'Esta obrigação não se aplica ao período. Uma decisão de aplicabilidade a fez sair da lista.',
      readOnly:
        'Você pode ver o andamento. Para avançar a ocorrência, peça a permissão de registrar evidência.',
      closeNote:
        'A ocorrência foi aceita. Encerre quando a conferência terminar ou abra uma retificação.',
    },
    evidence: {
      intro: 'Esta etapa só avança com a prova de que aconteceu em produção.',
      environment: 'Ambiente',
      environmentHelp: 'Só a produção conclui a etapa.',
      env: {
        PRODUCTION: 'Produção',
        RESTRICTED_PRODUCTION: 'Produção restrita (homologação)',
        SIMULATED: 'Simulado',
      },
      field: {
        fileVersion: 'Versão do arquivo',
        layoutVersion: 'Versão do leiaute',
        protocolNumber: 'Protocolo ou recibo',
        authorityReturn: 'Retorno da autoridade',
      },
      attachments: 'Anexos',
      attachmentsHelp: 'Recibo, arquivo gerado ou o retorno em PDF, XML ou texto, até 10 MB.',
      addFile: 'Anexar arquivo',
      removeFile: 'Remover {{name}}',
      uploading: 'Enviando o arquivo',
      uploadFailure: {
        failed: 'O arquivo não foi anexado. Anexe o arquivo de novo para seguir com a etapa.',
        invalid:
          'O arquivo não foi anexado: é grande demais ou é de um tipo que não aceitamos. Anexe outro arquivo para seguir com a etapa.',
      },
      notProduction:
        'Só evidência de produção conclui a etapa. Você pode registrar este teste no histórico sem avançar.',
      recordOnly: 'Registrar sem avançar',
    },
    payment: {
      title: 'Pagamento',
      help: 'O pagamento anda por conta própria: não depende de a obrigação ter sido entregue.',
      current: 'Situação do pagamento: {{state}}',
      state: {
        NOT_DUE: 'Não devido',
        DUE: 'A pagar',
        PAID: 'Pago',
        INSTALLMENT: 'Parcelado',
      },
      newState: 'Novo estado do pagamento',
      protocol: 'Guia ou comprovante',
      paidOn: 'Data do pagamento',
      paidOnError: 'Informe uma data válida.',
      paidHelp: 'Para registrar como pago, informe a data e o número da guia ou do comprovante.',
      reason: 'Observação',
      submit: 'Registrar pagamento',
      unchanged: 'Escolha um estado diferente do atual.',
    },
    rectification: {
      title: 'Retificação',
      help: 'A entrega aceita e a sua evidência ficam como estão. A retificação abre uma nova versão para preparar, validar, transmitir e aceitar de novo.',
      open: 'Abrir retificação',
      reason: 'Motivo da retificação',
      confirm: 'Abrir a nova versão',
      cancel: 'Cancelar',
    },
    history: {
      title: 'Histórico',
      empty: 'Nenhuma ação registrada ainda.',
      stateChanged: '{{from}} para {{to}}',
      paymentChanged: 'Pagamento: {{from}} para {{to}}',
      evidenceRecorded: 'Evidência registrada sem avançar',
      deadlineChanged: 'Prazo alterado',
      rectificationOpened: 'Retificação aberta: versão {{version}}',
      by: 'por {{name}}',
      byAt: '{{by}} · {{date}}',
      unknownAuthor: 'autor desconhecido',
      systemAuthor: 'Sistema',
      reason: 'Motivo: {{reason}}',
      environment: 'Ambiente: {{environment}}',
      protocol: 'Protocolo ou recibo: {{value}}',
      paymentDate: 'Data do pagamento: {{date}}',
      fileVersion: 'Arquivo: {{value}}',
      layoutVersion: 'Leiaute: {{value}}',
      authorityReturn: 'Retorno: {{value}}',
      download: 'Baixar {{name}}',
    },
    errors: {
      generic: 'Não foi possível concluir a ação. Tente novamente.',
      OBLIGATION_OCCURRENCE_NOT_FOUND: 'Não encontramos esta ocorrência. Atualize a página.',
      OBLIGATION_TRANSITION_NOT_ALLOWED:
        'A ocorrência não pode ir para esse estado agora. Atualize a página e avance um passo de cada vez.',
      OBLIGATION_NOT_APPLICABLE_BY_DECISION:
        'Uma ocorrência só deixa de se aplicar por decisão de aplicabilidade da obrigação.',
      OBLIGATION_EVIDENCE_REQUIRED: 'Esta etapa exige o ambiente e a prova que ela pede.',
      OBLIGATION_EVIDENCE_NOT_PRODUCTION:
        'Só evidência de produção conclui a etapa. Registre o teste sem avançar.',
      OBLIGATION_RECTIFICATION_NOT_ALLOWED:
        'Só uma ocorrência aceita ou encerrada pode ser retificada.',
      OBLIGATION_RECTIFICATION_REASON_REQUIRED: 'Informe o motivo da retificação.',
      OBLIGATION_PAYMENT_NOT_APPLICABLE: 'Uma ocorrência que não se aplica não tem pagamento.',
      OBLIGATION_PAYMENT_STATE_UNCHANGED: 'A ocorrência já está nesse estado de pagamento.',
      OBLIGATION_PAYMENT_DATE_REQUIRED:
        'Informe a data em que o pagamento foi feito para registrá-lo como pago.',
      OBLIGATION_PAYMENT_EVIDENCE_REQUIRED:
        'Informe o número da guia ou do comprovante para registrar o pagamento como pago.',
      OBLIGATION_ATTACHMENT_NOT_FOUND:
        'Não encontramos o anexo para a versão atual. Envie o arquivo novamente.',
      OBLIGATION_ATTACHMENT_LOCKED:
        'Um anexo que já faz parte de uma evidência não pode ser removido.',
      OBLIGATION_ATTACHMENT_INVALID:
        'O arquivo não foi enviado, é grande demais ou é de um tipo que não aceitamos.',
      OBLIGATION_RETRY: 'A ocorrência mudou durante o salvamento. Tente novamente.',
    },
  },
  competence: 'Competência',
  competenceAll: 'Todas as competências',
  competenceCustom: 'Período personalizado',
  readinessLoadError: 'Não foi possível carregar a configuração das empresas.',
  indicatorsLabel: 'Resumo das obrigações',
  indicator: {
    dueSoon: 'Vencem em 7 dias',
    overdue: 'Atrasadas',
    unassigned: 'Sem responsável',
    undecided: 'Sem decisão',
  },
  group: {
    overdue: 'Atrasadas',
    thisWeek: 'Esta semana',
    nextWeek: 'Próxima semana',
    later: 'Depois',
    noDue: 'Sem vencimento',
  },
  nextAction: {
    prepare: 'Preparar',
    validate: 'Validar',
    transmit: 'Transmitir',
    assign: 'Definir responsável',
    view: 'Ver',
    decide: 'Decidir',
  },
  bulk: {
    applies: 'Se aplica',
    nounOne: 'obrigação',
    nounOther: 'obrigações',
    noCompanyAccess:
      'Há obrigações de uma empresa com filiais fora do seu acesso: peça a quem administra a empresa',
  },
  applyDialog: {
    title_one: 'Marcar {{count}} obrigação como "Se aplica"',
    title_other: 'Marcar {{count}} obrigações como "Se aplica"',
    description:
      'Cada obrigação passa a valer na data abaixo. Para "Não se aplica", decida uma por vez na aba Obrigações da empresa.',
    validFrom: 'A partir de',
    confirm: 'Marcar "Se aplica"',
    cancel: 'Cancelar',
    close: 'Fechar',
    partial_one: '{{count}} obrigação não foi marcada. Veja o motivo abaixo.',
    partial_other: '{{count}} obrigações não foram marcadas. Veja os motivos abaixo.',
    refusedLabel: 'Obrigações não marcadas',
    rowError: 'Não foi possível marcar esta obrigação.',
    error: 'Não foi possível marcar as obrigações. Tente novamente.',
    tooMany: 'Selecione no máximo {{max}} obrigações por vez.',
  },
  setup: {
    title: 'Configure as obrigações de cada empresa',
    description:
      'Termine os passos de cada empresa. Depois, a central mostra o que vence, o que atrasou e quem cuida de cada entrega.',
    progress: '{{done}} de {{total}}',
    next: 'Próximo passo',
    done: 'Feito',
    noAccess: 'Peça a quem administra o cadastro da empresa',
    ready: 'Pronta',
    step: {
      taxpayer: 'Contribuinte e matriz',
      history: 'Histórico fiscal',
      cbsIbs: 'Tratamento de CBS e IBS',
      accountant: 'Contador responsável',
      screening: 'Triagem',
      decide: 'Decidir obrigações',
    },
    stepDetail: {
      screening_one: '{{count}} pergunta sem resposta',
      screening_other: '{{count}} perguntas sem resposta',
      decide_one: '{{count}} obrigação sem decisão',
      decide_other: '{{count}} obrigações sem decisão',
    },
    action: {
      taxpayer: 'Completar cadastro',
      history: 'Informar histórico',
      cbsIbs: 'Definir tratamento',
      accountant: 'Escolher contador',
      screening: 'Responder',
      decide: 'Decidir {{count}}',
    },
  },
  notReady_one: '{{count}} empresa ainda não está pronta: {{name}} ({{done}} de {{total}} passos)',
  notReady_other:
    '{{count}} empresas ainda não estão prontas. A primeira: {{name}} ({{done}} de {{total}} passos)',
  continueSetup: 'Continuar configuração',
  nounOne: 'ocorrência',
  nounOther: 'ocorrências',
} as const;

type Widen<T> = T extends string ? string : { [K in keyof T]: Widen<T[K]> };

export const obligationsCentralEn: Widen<typeof obligationsCentralPtBr> = {
  title: 'Obligations central',
  subtitle:
    'Follow what is due, what is late and what is still unclassified. Configuration is done on the Obligations tab of each company.',
  tableLabel: 'Obligations by period',
  gapsTableLabel: 'Classification gaps',
  loading: 'Loading the companies',
  columns: {
    obligation: 'Obligation',
    obligationPeriod: 'Obligation · period',
    companyEstablishment: 'Company · establishment',
    due: 'Due date',
    state: 'Status',
    responsible: 'Responsible',
    decision: 'Decision',
    reason: 'Reason',
  },
  state: {
    AWAITING_FACTS: 'Awaiting facts',
    PENDING: 'Pending',
    PREPARED: 'Prepared',
    VALIDATED: 'Validated',
    TRANSMITTED: 'Transmitted',
    ACCEPTED: 'Accepted',
    RECTIFICATION_PENDING: 'Rectification pending',
    CLOSED: 'Closed',
    NOT_APPLICABLE: 'Not applicable',
  },
  gapState: {
    PENDING_CLASSIFICATION: 'Pending classification',
    OUT_OF_SCOPE_FLAGGED: 'Out of scope, flagged',
  },
  due: {
    none: 'No fixed deadline',
    overdue: 'Overdue',
    source: 'Source: {{source}} (rule {{version}})',
    overrideSource: 'Company deadline. Source: {{source}}',
  },
  responsible: {
    none: 'No one responsible',
    vacant: 'No one to {{roles}}',
    role: {
      PREPARER: 'prepare',
      APPROVER: 'approve',
      TRANSMITTER: 'transmit',
      PAYER: 'pay',
    },
  },
  gap: {
    noDecision: 'No decision',
    since: 'since {{date}}',
  },
  empty: {
    title: 'No obligations in this period',
    description:
      'Occurrences appear once an obligation is decided as applicable on the company Obligations tab.',
    gapsTitle: 'No gaps',
    gapsDescription: 'Every obligation has a decision the ERP can fulfil.',
    dueSoonTitle: 'Nothing due in the next 7 days',
    overdueTitle: 'No overdue obligations',
    unassignedTitle: 'Every open obligation has someone responsible',
    indicatorDescription: 'Click the chosen indicator to see every open obligation.',
  },
  loadError: 'Could not load the obligations. Try again.',
  retry: 'Try again',
  linkLabel: '{{action}}: {{name}}',
  sheet: {
    title: '{{obligation}} · {{period}}',
    description: '{{company}} · {{establishment}}',
    loading: 'Loading the occurrence',
    loadError: 'Could not load the occurrence.',
    refreshError: 'Could not refresh the occurrence. What you typed is kept.',
    version: 'Version {{version}}',
    versionCurrent: 'Version {{version}} (current)',
    summary: {
      due: 'Due date',
      noDue: 'No fixed deadline',
      overdue: 'Overdue',
      dueDate: '{{date}}',
      dueOverdue: '{{date}} · Overdue',
      channel: 'Delivery channel',
      source: 'Deadline source',
      sourceValue: '{{source}} (rule {{version}})',
      overrideValue: 'Company deadline: {{source}}',
      responsibles: 'Responsible',
      vacant: 'No one to {{roles}}',
      roleLine: '{{role}}: {{name}}',
    },
    roleLabel: {
      PREPARER: 'Prepare',
      APPROVER: 'Approve',
      TRANSMITTER: 'Transmit',
      PAYER: 'Pay',
    },
    next: {
      title: 'Next step',
      advance: 'Move to {{state}}',
      reason: 'Reason or note',
      reasonHelp: 'Optional. Kept in the history.',
      noAction: 'This occurrence has no next step.',
      notApplicable:
        'This obligation does not apply to the period. An applicability decision took it off the list.',
      readOnly:
        'You can see the progress. To move the occurrence, ask for the permission to record evidence.',
      closeNote:
        'The occurrence was accepted. Close it when the review is done, or open a rectification.',
    },
    evidence: {
      intro: 'This step only moves on with proof that it happened in production.',
      environment: 'Environment',
      environmentHelp: 'Only production concludes the step.',
      env: {
        PRODUCTION: 'Production',
        RESTRICTED_PRODUCTION: 'Restricted production (testing)',
        SIMULATED: 'Simulated',
      },
      field: {
        fileVersion: 'File version',
        layoutVersion: 'Layout version',
        protocolNumber: 'Protocol or receipt',
        authorityReturn: 'Authority answer',
      },
      attachments: 'Attachments',
      attachmentsHelp: 'Receipt, generated file or the answer as PDF, XML or text, up to 10 MB.',
      addFile: 'Attach a file',
      removeFile: 'Remove {{name}}',
      uploading: 'Uploading the file',
      uploadFailure: {
        failed: 'The file was not attached. Attach the file again to go on with the step.',
        invalid:
          'The file was not attached: it is too large or of a type we do not accept. Attach another file to go on with the step.',
      },
      notProduction:
        'Only production evidence concludes the step. You can record this test in the history without moving on.',
      recordOnly: 'Record without moving on',
    },
    payment: {
      title: 'Payment',
      help: 'Payment moves on its own: it does not depend on the obligation having been delivered.',
      current: 'Payment status: {{state}}',
      state: {
        NOT_DUE: 'Not due',
        DUE: 'To pay',
        PAID: 'Paid',
        INSTALLMENT: 'In installments',
      },
      newState: 'New payment status',
      protocol: 'Payment slip or receipt',
      paidOn: 'Payment date',
      paidOnError: 'Enter a valid date.',
      paidHelp: 'To record it as paid, enter the date and the payment slip or receipt number.',
      reason: 'Note',
      submit: 'Record payment',
      unchanged: 'Pick a status different from the current one.',
    },
    rectification: {
      title: 'Rectification',
      help: 'The accepted delivery and its evidence stay as they are. A rectification opens a new version to prepare, validate, transmit and accept again.',
      open: 'Open rectification',
      reason: 'Reason for the rectification',
      confirm: 'Open the new version',
      cancel: 'Cancel',
    },
    history: {
      title: 'History',
      empty: 'No action recorded yet.',
      stateChanged: '{{from}} to {{to}}',
      paymentChanged: 'Payment: {{from}} to {{to}}',
      evidenceRecorded: 'Evidence recorded without moving on',
      deadlineChanged: 'Deadline changed',
      rectificationOpened: 'Rectification opened: version {{version}}',
      by: 'by {{name}}',
      byAt: '{{by}} · {{date}}',
      unknownAuthor: 'unknown author',
      systemAuthor: 'System',
      reason: 'Reason: {{reason}}',
      environment: 'Environment: {{environment}}',
      protocol: 'Protocol or receipt: {{value}}',
      paymentDate: 'Payment date: {{date}}',
      fileVersion: 'File: {{value}}',
      layoutVersion: 'Layout: {{value}}',
      authorityReturn: 'Answer: {{value}}',
      download: 'Download {{name}}',
    },
    errors: {
      generic: 'Could not complete the action. Try again.',
      OBLIGATION_OCCURRENCE_NOT_FOUND: 'We could not find this occurrence. Refresh the page.',
      OBLIGATION_TRANSITION_NOT_ALLOWED:
        'The occurrence cannot move to that state now. Refresh the page and move one step at a time.',
      OBLIGATION_NOT_APPLICABLE_BY_DECISION:
        'An occurrence only stops applying through an applicability decision of the obligation.',
      OBLIGATION_EVIDENCE_REQUIRED: 'This step needs the environment and the proof it asks for.',
      OBLIGATION_EVIDENCE_NOT_PRODUCTION:
        'Only production evidence concludes the step. Record the test without moving on.',
      OBLIGATION_RECTIFICATION_NOT_ALLOWED:
        'Only an accepted or closed occurrence can be rectified.',
      OBLIGATION_RECTIFICATION_REASON_REQUIRED: 'Enter the reason for the rectification.',
      OBLIGATION_PAYMENT_NOT_APPLICABLE: 'An occurrence that does not apply has no payment.',
      OBLIGATION_PAYMENT_STATE_UNCHANGED: 'The occurrence already has that payment status.',
      OBLIGATION_PAYMENT_DATE_REQUIRED: 'Enter the day the payment was made to record it as paid.',
      OBLIGATION_PAYMENT_EVIDENCE_REQUIRED:
        'Enter the payment slip or receipt number to record the payment as paid.',
      OBLIGATION_ATTACHMENT_NOT_FOUND:
        'We could not find the attachment for the current version. Upload the file again.',
      OBLIGATION_ATTACHMENT_LOCKED:
        'An attachment that is already part of evidence cannot be removed.',
      OBLIGATION_ATTACHMENT_INVALID:
        'The file was not sent, is too large or is of a type we do not accept.',
      OBLIGATION_RETRY: 'The occurrence changed while saving. Try again.',
    },
  },
  competence: 'Period',
  competenceAll: 'All periods',
  competenceCustom: 'Custom period',
  readinessLoadError: "Could not load the companies' setup.",
  indicatorsLabel: 'Obligations summary',
  indicator: {
    dueSoon: 'Due in 7 days',
    overdue: 'Overdue',
    unassigned: 'No one responsible',
    undecided: 'Not decided',
  },
  group: {
    overdue: 'Overdue',
    thisWeek: 'This week',
    nextWeek: 'Next week',
    later: 'Later',
    noDue: 'No due date',
  },
  nextAction: {
    prepare: 'Prepare',
    validate: 'Validate',
    transmit: 'Transmit',
    assign: 'Assign people',
    view: 'View',
    decide: 'Decide',
  },
  bulk: {
    applies: 'Applies',
    nounOne: 'obligation',
    nounOther: 'obligations',
    noCompanyAccess:
      'Some obligations belong to a company with establishments outside your access: ask whoever manages the company',
  },
  applyDialog: {
    title_one: 'Mark {{count}} obligation as "Applies"',
    title_other: 'Mark {{count}} obligations as "Applies"',
    description:
      'Each obligation applies from the date below. For "Does not apply", decide one at a time on the company\'s Obligations tab.',
    validFrom: 'From',
    confirm: 'Mark "Applies"',
    cancel: 'Cancel',
    close: 'Close',
    partial_one: '{{count}} obligation was not marked. See the reason below.',
    partial_other: '{{count}} obligations were not marked. See the reasons below.',
    refusedLabel: 'Obligations not marked',
    rowError: 'Could not mark this obligation.',
    error: 'Could not mark the obligations. Try again.',
    tooMany: 'Select at most {{max}} obligations at a time.',
  },
  setup: {
    title: 'Set up the obligations of each company',
    description:
      'Finish the steps of each company. Then the central shows what is due, what is late and who handles each filing.',
    progress: '{{done}} of {{total}}',
    next: 'Next step',
    done: 'Done',
    noAccess: "Ask whoever manages the company's settings",
    ready: 'Ready',
    step: {
      taxpayer: 'Taxpayer and head office',
      history: 'Tax history',
      cbsIbs: 'CBS and IBS treatment',
      accountant: 'Responsible accountant',
      screening: 'Screening',
      decide: 'Decide obligations',
    },
    stepDetail: {
      screening_one: '{{count}} question not answered',
      screening_other: '{{count}} questions not answered',
      decide_one: '{{count}} obligation not decided',
      decide_other: '{{count}} obligations not decided',
    },
    action: {
      taxpayer: 'Complete the record',
      history: 'Enter the history',
      cbsIbs: 'Set the treatment',
      accountant: 'Choose an accountant',
      screening: 'Answer',
      decide: 'Decide {{count}}',
    },
  },
  notReady_one: '{{count}} company is not ready yet: {{name}} ({{done}} of {{total}} steps)',
  notReady_other:
    '{{count}} companies are not ready yet. The first: {{name}} ({{done}} of {{total}} steps)',
  continueSetup: 'Continue setup',
  nounOne: 'occurrence',
  nounOther: 'occurrences',
};
